import { useAui } from '@assistant-ui/react';
import { Button, type ButtonProps } from '@mantine/core';
import { IconSparklesFilled } from '@tabler/icons-react';

import classes from './CreateResponseTool.module.css';
import { type CreateResponseInput, CreateResponseInputSchema, type CreateResponseOutput } from './schema.ts';
import { StreamingMarkdown } from '#plugin-chat/components/markdown-text/StreamingMarkdown.tsx';
import { ToolAside } from '#plugin-chat/components/tool-display/ToolAside.tsx';
import { ToolContentBubble } from '#plugin-chat/components/tool-display/ToolContentBubble.tsx';
import type { ToolProps } from '#plugin-chat/components/tools/Tool.ts';
import { ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { Tool } from '#plugin-chat/lib/tool/Tool.tsx';
import type { ToolResult } from '#plugin-chat/lib/transport/types.ts';

export function CreateResponseTool(props: ToolProps) {
  return (
    <Tool<CreateResponseInput, CreateResponseOutput>
      description={
        <ToolGuidance
          description="Call this tool to provide a text-based response to the user."
          whenNotToUse={[
            'To display a short ranked list of key insights based on prior tool results — use ShowInsightsTool instead',
            'To ask the user a question — use AskUserQuestionTool instead',
          ]}
          whenToUse={[
            'Final or end-of-turn informational message to the user',
            'In-progress or mid-turn informational message interleaved between other tool calls to inform the user about planned actions or intermediate results',
            'Reporting an error to the user',
          ]}
        />
      }
      execute={({ response: { type } }) =>
        ({
          success: true,
          flowSignal: {
            continuation: type === 'final' ? 'stop' : 'continue',
          },
        }) satisfies ToolResult
      }
      message={{
        // The response IS the assistant's prose — it renders as a real
        // `assistant` turn instead of a tool-call record envelope.
        transcript: ({ args }) => <transcript-message role="assistant">{args.response?.message}</transcript-message>,
      }}
      parameters={CreateResponseInputSchema}
      render={{
        running: ({ args }) => <ResponseContent args={args} />,
        complete: ({ args }) => <ResponseContent args={args} />,
      }}
      {...props}
    />
  );
}

/**
 * Shared running/complete body: the streamed response markdown in the
 * content bubble (error-tinted for final error responses) plus follow-up
 * suggestion buttons portalled outside the bezel.
 */
function ResponseContent({ args: { response } }: { args: Partial<CreateResponseInput> }) {
  return (
    <>
      <ToolContentBubble kind={response?.type === 'final' && response.kind === 'error' ? 'error' : undefined}>
        <div className={classes.message}>
          {response?.message !== undefined && <StreamingMarkdown text={response.message} />}
        </div>
      </ToolContentBubble>
      {response?.type === 'final' && !!response.nextSteps?.length && (
        <ToolAside>
          <div className={classes.followUpQuestions}>
            {response.nextSteps.map((question, index) => (
              <SuggestionButton key={question} prompt={question} style={{ '--index': index }} />
            ))}
          </div>
        </ToolAside>
      )}
    </>
  );
}

// FIXME: separate component
function SuggestionButton({ prompt, ...props }: ButtonProps & { prompt: string }) {
  const aui = useAui();

  const trigger = () => {
    aui.thread().append({
      content: [{ type: 'text', text: prompt }],
      runConfig: aui.composer().getState().runConfig,
    });

    aui.composer().setText('');
  };

  return (
    <Button
      classNames={{ label: classes.optionButtonLabel, root: classes.optionButton }}
      color="dark.6"
      leftSection={<IconSparklesFilled color="var(--mantine-color-dark-2)" size={16} />}
      onClick={trigger}
      radius="sm"
      variant="light"
      {...props}
    >
      <span className={classes.optionContent}>
        <span className={classes.optionLabel}>{prompt}</span>
        {/* {option.description && <span className={classes.optionDescription}>{option.description}</span>} */}
      </span>
    </Button>
  );
}
